import { AfterViewInit, Component, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from '../../services/sharedService';
import { IssueTrackerService, TrackerSettings } from '../../services/issueTracker.service';

/**
 * Where an administrator sets the issue tracker's API key.
 *
 * The key is write-only from here. The server never returns it -- only whether
 * one is set, where it came from and its last four characters -- so this screen
 * cannot display it, and an empty field means "leave it as it is" rather than
 * "clear it". Clearing is a separate, deliberate action.
 */
@Component({
    selector: 'app-issue-tracker-settings',
    templateUrl: './issue-tracker-settings.component.html',
    standalone: false,
})
export class IssueTrackerSettingsComponent implements OnInit, AfterViewInit {
    settings: TrackerSettings | null = null;
    apiKey = '';
    readApiKey = '';
    baseUrl = '';
    saving = false;
    loading = true;

    constructor(
        private issueTracker: IssueTrackerService,
        private alertService: ToastrService,
        private translate: TranslateService,
        private sharedService: SharedService,
    ) {}

    ngOnInit(): void {
        this.load();
    }

    ngAfterViewInit(): void {
        this.sharedService.loadMenuItems('admin');
    }

    private load(): void {
        this.loading = true;
        this.issueTracker.getSettings().subscribe({
            next: (settings) => {
                this.settings = settings;
                this.baseUrl = settings.baseUrl || '';
                this.apiKey = '';
                this.readApiKey = '';
                this.loading = false;
            },
            error: () => {
                this.loading = false;
                this.alertService.error(this.translate.instant('ISSUE.SETTINGS.LOAD_FAILED'));
            },
        });
    }

    save(): void {
        if (this.saving) return;
        this.saving = true;

        const payload: { apiKey?: string; readApiKey?: string; baseUrl?: string } = {};
        // Only send a key when one was typed. An empty field is not a request to
        // remove the stored key -- the field starts empty on every load.
        if (this.apiKey.trim()) payload.apiKey = this.apiKey.trim();
        if (this.readApiKey.trim()) payload.readApiKey = this.readApiKey.trim();
        if (this.baseUrl.trim()) payload.baseUrl = this.baseUrl.trim();

        this.issueTracker.saveSettings(payload).subscribe({
            next: (settings) => {
                this.saving = false;
                this.settings = settings;
                this.baseUrl = settings.baseUrl || '';
                this.apiKey = '';
                this.readApiKey = '';
                this.alertService.success(this.translate.instant('ISSUE.SETTINGS.SAVED'));
            },
            error: (err) => {
                this.saving = false;
                // The server refuses an address it cannot make absolute, which is
                // the one save failure an administrator can actually act on.
                const code = err && err.error && err.error.error;
                this.alertService.error(this.translate.instant(
                    code === 'TRACKER_URL_INVALID' ? 'ISSUE.ERROR.TRACKER_URL_INVALID' : 'ISSUE.SETTINGS.SAVE_FAILED'));
            },
        });
    }

    clearKey(): void {
        if (this.saving) return;
        this.saving = true;
        this.issueTracker.saveSettings({ clearKey: true }).subscribe({
            next: (settings) => {
                this.saving = false;
                this.settings = settings;
                this.apiKey = '';
                this.alertService.success(this.translate.instant('ISSUE.SETTINGS.CLEARED'));
            },
            error: () => {
                this.saving = false;
                this.alertService.error(this.translate.instant('ISSUE.SETTINGS.SAVE_FAILED'));
            },
        });
    }
}
